import { getAuthHeaders } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Dashboard Stats
export const getDashboardStats = async () => {
  const response = await fetch(`${API_URL}/api/admin/dashboard/stats?ts=${Date.now()}`, {
    headers: {
      ...getAuthHeaders(),
      'Cache-Control': 'no-cache'
    },
    cache: 'no-store'
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch dashboard stats');
  }
  
  return response.json();
};

// User Management
export const getUsers = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  accountType?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, value.toString());
  });
  
  const response = await fetch(`${API_URL}/api/admin/users?${queryParams}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }
  
  return response.json();
};

export const getUserById = async (userId: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user details');
  }
  
  return response.json();
};

export const updateUser = async (userId: string, updates: any) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update user');
  }
  
  return response.json();
};

export const deleteUser = async (userId: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete user');
  }
  
  return response.json();
};

export const suspendUser = async (userId: string, reason?: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/suspend`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ reason })
  });
  
  if (!response.ok) {
    throw new Error('Failed to suspend user');
  }
  
  return response.json();
};

export const activateUser = async (userId: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/activate`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to activate user');
  }
  
  return response.json();
};

// Subscription Management
export const getUserSubscription = async (userId: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/subscription`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user subscription');
  }
  
  return response.json();
};

// License & Credits Management
export const setUserLicense = async (userId: string, license: 'free'|'commercial'|'template_pack'|'professional'|'agency') => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/license`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ license })
  });
  if (!response.ok) throw new Error('Failed to set user license');
  return response.json();
};

export const creditUser = async (userId: string, amount: number, reason?: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/credits`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ amount, reason })
  });
  if (!response.ok) throw new Error('Failed to credit user');
  return response.json();
};

export const updateUserSubscription = async (userId: string, updates: {
  accountType?: string;
  expiresAt?: string;
  monthlyLimit?: number;
}) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/subscription`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update subscription');
  }
  
  return response.json();
};

export const extendSubscription = async (userId: string, days: number) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/subscription/extend`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ days })
  });
  
  if (!response.ok) {
    throw new Error('Failed to extend subscription');
  }
  
  return response.json();
};

export const cancelSubscription = async (userId: string, reason?: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/subscription/cancel`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ reason })
  });
  
  if (!response.ok) {
    throw new Error('Failed to cancel subscription');
  }
  
  return response.json();
};

export const addBonusGenerations = async (userId: string, amount: number) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/generations/add`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ amount })
  });
  
  if (!response.ok) {
    throw new Error('Failed to add bonus generations');
  }
  
  return response.json();
};

// User Activity & Stats
export const getUserActivity = async (userId: string, limit: number = 50) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/activity?limit=${limit}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user activity');
  }
  
  return response.json();
};

export const getUserGenerations = async (userId: string, page: number = 1, limit: number = 20) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/generations?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user generations');
  }
  
  return response.json();
};

export const getUserStats = async (userId: string, days: number = 30) => {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}/stats?days=${days}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user stats');
  }
  
  return response.json();
};

// Generation Statistics
export const getGenerationStats = async (days: number = 30) => {
  const response = await fetch(`${API_URL}/api/admin/generations/stats?days=${days}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch generation stats');
  }
  
  return response.json();
};

export const getTemplateUsageStats = async (days: number = 30) => {
  const response = await fetch(`${API_URL}/api/admin/generations/templates?days=${days}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch template usage stats');
  }
  
  return response.json();
};

// Audit Log
export const getAuditLog = async (params: {
  page?: number;
  limit?: number;
  adminId?: string;
  targetUserId?: string;
  actionType?: string;
}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, value.toString());
  });
  
  const response = await fetch(`${API_URL}/api/admin/audit-log?${queryParams}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch audit log');
  }
  
  return response.json();
};

// Bulk Operations
export const exportUsers = async (filters?: {
  accountType?: string;
  status?: string;
}) => {
  const response = await fetch(`${API_URL}/api/admin/users/bulk/export`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(filters || {})
  });
  
  if (!response.ok) {
    throw new Error('Failed to export users');
  }
  
  // Return blob for CSV download
  return response.blob();
};

export const bulkNotifyUsers = async (userIds: string[], subject: string, message: string) => {
  const response = await fetch(`${API_URL}/api/admin/users/bulk/notify`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ userIds, subject, message })
  });
  
  if (!response.ok) {
    throw new Error('Failed to send notifications');
  }
  
  return response.json();
};
